import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getEmployees, deleteEmployee, exportEmployees } from "./EmployeeApi";
import { downloadBlob } from "./employeeFileUtils";
import { OnboardingContext } from "../Context/OnboardingContext";
import SetupIncompleteBanner from "../../components/common/SetupIncompleteBanner";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import { invalidateEmployeesCache } from "../../hooks/useEmployees";
import { invalidateDashboardOverview } from "../../services/dashboardApi";
import API from "../../services/api";

/** Types shown in the list page filter dropdown only */
const LIST_FILTER_TYPES = ["Permanent", "Temporary", "Contract", "Intern"];

const TYPE_BADGE_CLASS = {
  Permanent: "type-permanent",
  Temporary: "type-temporary",
  Contract: "type-contract",
  Intern: "type-intern",
};

// ─── Icons (inline SVG, no emoji) ────────────────────────────────────────────

const IconView = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const IconAlertTriangle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const IconLoader = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spin-icon">
    <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
    <line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
  </svg>
);

const IconFilter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const IconUser = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconUsers = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

// ─── Toast Component ──────────────────────────────────────────────────────────

function Toast({ toasts, removeToast }) {
  return (
    <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, minWidth: 320, maxWidth: 420 }}>
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.type}`}
          style={{ animation: "slideInRight 0.3s ease" }}
        >
          <span className="toast-icon">
            {t.type === "success" ? <IconCheck /> : <IconAlertTriangle />}
          </span>
          <span className="toast-msg">{t.message}</span>
          <button className="toast-close" onClick={() => removeToast(t.id)}><IconClose /></button>
        </div>
      ))}
    </div>
  );
}

// ─── Delete Confirmation Modal ────────────────────────────────────────────────

function DeleteModal({ employee, onConfirm, onCancel, loading }) {
  const fullName = [employee.first_name, employee.middle_name, employee.last_name].filter(Boolean).join(" ");

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-icon-wrap">
          <IconAlertTriangle />
        </div>
        <h3 className="modal-title">Delete Employee</h3>
        <p className="modal-body">
          Are you sure you want to delete employee{" "}
          <strong>{fullName}</strong>? This action cannot be undone.
        </p>
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? <><IconLoader /> Deleting…</> : "Delete Employee"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TypeBadge({ type }) {
  const label = type || "Permanent";
  const cls = TYPE_BADGE_CLASS[label] || "type-default";
  return <span className={`el-type-badge ${cls}`}>{label}</span>;
}

function fullNameFromEmp(emp) {
  return [emp?.first_name, emp?.middle_name, emp?.last_name].filter(Boolean).join(" ");
}

function getInitials(emp) {
  const a = emp?.first_name?.[0] || "";
  const b = emp?.last_name?.[0] || "";
  return (a + b).toUpperCase() || "?";
}

function managerLabel(emp) {
  if (emp?.reporting_manager_employee?.employee_name) return emp.reporting_manager_employee.employee_name;
  if (emp?.reporting_manager?.full_name) return emp.reporting_manager.full_name;
  if (emp?.reporting_manager?.email) return emp.reporting_manager.email;
  return "—";
}

function PageLoader() {
  return <BrixigoPageLoader message="Loading employees…" />;
}

function PreviewField({ label, value }) {
  return (
    <div className="preview-field">
      <span className="preview-field-label">{label}</span>
      <span className="preview-field-value">{value || "—"}</span>
    </div>
  );
}

function EmployeePreviewDrawer({ employee, onClose, onOpenFull }) {
  if (!employee) return null;
  const name = fullNameFromEmp(employee) || "Employee";

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} aria-hidden />
      <aside
        className="drawer preview-drawer"
        role="dialog"
        aria-label={`Preview ${name}`}
      >
        <div className="drawer-header preview-drawer-header">
          <div className="drawer-avatar preview-avatar">{getInitials(employee)}</div>
          <div className="preview-header-text">
            <div className="drawer-title">{name}</div>
            <div className="drawer-subtitle">{employee.employee_code || "No code"}</div>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="Close preview">
            <IconClose />
          </button>
        </div>

        <div className="drawer-body">
          <div className="drawer-section-label">Employee details</div>
          <div className="preview-grid">
            <PreviewField label="Employee code" value={employee.employee_code} />
            <PreviewField label="Type" value={employee.employee_type || "Permanent"} />
            <PreviewField label="Work email" value={employee.work_email || employee.email} />
            <PreviewField label="Department" value={employee.department?.name} />
            <PreviewField label="Designation" value={employee.designation?.title} />
            <PreviewField label="Location" value={employee.location?.name} />
            <PreviewField label="Reporting manager" value={managerLabel(employee)} />
            <PreviewField
              label="Status"
              value={
                employee.status
                  ? employee.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                  : "—"
              }
            />
          </div>
        </div>

        <div className="drawer-footer preview-drawer-footer">
          <button type="button" className="btn-preview-ghost" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn-preview-primary" onClick={() => onOpenFull(employee)}>
            Open full record →
          </button>
        </div>
      </aside>
    </>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    active: "badge-active",
    inactive: "badge-inactive",
    on_leave: "badge-leave",
    terminated: "badge-terminated",
  };
  return (
    <span className={`badge ${map[status] || "badge-inactive"}`}>
      {status ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Unknown"}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EmployeeList({
  embedded = false,
  listRefreshKey = 0,
  onRecordCountChange,
  onAddEmployee,
  onBulkImport,
  onEditEmployee,
  missingVerifiedBankOnly = false,
}) {
  const obCtx = useContext(OnboardingContext);
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeOnly, setActiveOnly] = useState(true);
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);
  const [previewEmp, setPreviewEmp] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Action loading (per row)
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [fuelEnabled, setFuelEnabled] = useState(false);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const toastCounter = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    API.get("/fuel-reimbursement/settings")
      .then((res) => {
        if (!cancelled) setFuelEnabled(Boolean(res.data?.enabled));
      })
      .catch(() => {
        if (!cancelled) setFuelEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addToast = useCallback((message, type = "success") => {
    const id = ++toastCounter.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4500);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, rowsPerPage, activeOnly, employeeTypeFilter, missingVerifiedBankOnly]);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getEmployees({
        page: currentPage,
        page_size: rowsPerPage,
        search: debouncedSearch || undefined,
        active_only: activeOnly,
        employee_type: employeeTypeFilter.length ? employeeTypeFilter : undefined,
        missing_verified_bank: missingVerifiedBankOnly || undefined,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = typeof data?.total === "number" ? data.total : items.length;
      setEmployees(items);
      setTotalCount(total);

      if (items.length === 0 && currentPage > 1 && total > 0) {
        setCurrentPage((p) => Math.max(1, p - 1));
      }
    } catch (err) {
      addToast("Failed to load employees. Please refresh.", "error");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearch,
    activeOnly,
    employeeTypeFilter,
    missingVerifiedBankOnly,
    addToast,
  ]);

  const toggleEmployeeTypeFilter = (type) => {
    setEmployeeTypeFilter((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      setExportMenuOpen(false);
      const blob = await exportEmployees(format, {
        active_only: activeOnly,
        employee_type: employeeTypeFilter.length ? employeeTypeFilter : undefined,
      });
      downloadBlob(
        blob,
        format === "xlsx" ? "employees_export.xlsx" : "employees_export.csv"
      );
      addToast(format === "xlsx" ? "Excel export downloaded." : "CSV export downloaded.");
    } catch {
      addToast("Failed to export employees.", "error");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const onClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [exportMenuOpen]);

  useEffect(() => {
    if (!filterMenuOpen) return undefined;
    const onClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [filterMenuOpen]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees, listRefreshKey]);

  useEffect(() => {
    if (onRecordCountChange && !loading) {
      onRecordCountChange(totalCount);
    }
  }, [totalCount, loading, onRecordCountChange]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const firstIndex = totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const lastIndex = Math.min(currentPage * rowsPerPage, totalCount);
  const currentEmployees = employees;

  const fullName = fullNameFromEmp;

  const closePreview = useCallback(() => {
    setPreviewEmp(null);
  }, []);

  const goToProfile = useCallback(
    (emp) => {
      if (!emp?.employee_id || actionLoadingId) return;
      navigate(`/employees/${emp.employee_id}`);
    },
    [navigate, actionLoadingId]
  );

  const handleRowClick = useCallback(
    (emp) => {
      goToProfile(emp);
    },
    [goToProfile]
  );

  useEffect(() => {
    if (!previewEmp) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closePreview(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewEmp, closePreview]);

  const goToEdit = (emp) => {
    if (actionLoadingId) return;
    if (onEditEmployee) {
      onEditEmployee(emp.employee_id);
    } else {
      navigate(`/employees/${emp.employee_id}/edit`, {
        state: { returnTo: `${location.pathname}${location.search}` },
      });
    }
  };

  const handleAddClick = () => {
    if (onAddEmployee) onAddEmployee();
    else navigate("/employees/add");
  };

  const handleBulkClick = () => {
    if (onBulkImport) onBulkImport();
    else navigate("/employees?view=import");
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    const emp = deleteTarget;
    try {
      setDeleteLoading(true);
      setActionLoadingId(emp.employee_id);

      await deleteEmployee(emp.employee_id);

      setDeleteTarget(null);
      addToast(`Employee ${fullName(emp)} deleted successfully`, "success");
      invalidateEmployeesCache();
      invalidateDashboardOverview(queryClient);
      await fetchEmployees();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "Employee not found") {
        setDeleteTarget(null);
        addToast(`Employee ${fullName(emp)} was not found and has been removed from the list.`, "error");
        invalidateEmployeesCache();
        invalidateDashboardOverview(queryClient);
        await fetchEmployees();
      } else {
        addToast(`Failed to delete employee ${fullName(emp)}. Please try again.`, "error");
        setDeleteTarget(null);
      }
    } finally {
      setDeleteLoading(false);
      setActionLoadingId(null);
    }
  };

  // ── Page numbers ──────────────────────────────────────────────────────────

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages, currentPage + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  const activeFilterCount =
    employeeTypeFilter.length + (!activeOnly ? 1 : 0);

  const clearAllFilters = () => {
    setEmployeeTypeFilter([]);
    setActiveOnly(true);
    setFilterMenuOpen(false);
  };

  const designationLabel = (emp) =>
    emp.designation?.title || emp.designation?.name || "—";

  const phoneLabel = (emp) => emp.mobile_phone || emp.phone || "—";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #f0f4ff;
          --surface: #ffffff;
          --border: #dbeafe;
          --border-strong: #93c5fd;
          --text-primary: #0a0a0a;
          --text-secondary: #0a0a0a;
          --text-muted: #0a0a0a;
          --navy: #0f172a;
          --royal: #1e3a8a;
          --royal-mid: #2563eb;
          --accent: #1d4ed8;
          --accent-hover: #1e40af;
          --accent-light: #eff6ff;
          --danger: #dc2626;
          --danger-hover: #b91c1c;
          --danger-light: #fef2f2;
          --success: #16a34a;
          --success-light: #f0fdf4;
          --warning: #d97706;
          --update-flash: #dbeafe;
          --row-hover: #eff6ff;
          --row-stripe: #f8fafc;
          --row-active: #dbeafe;
          --font: 'IBM Plex Sans', sans-serif;
          --font-mono: 'IBM Plex Mono', monospace;
          --radius: 8px;
          --radius-lg: 14px;
          --shadow: 0 1px 3px rgba(0,0,0,.06), 0 4px 14px rgba(0,0,0,.04);
          --shadow-lg: 0 12px 32px rgba(0,0,0,.1);
          --shadow-xl: 0 20px 60px rgba(0,0,0,.14);
        }

        body { font-family: var(--font); background: var(--bg); color: var(--text-primary); }

        .el-page {
          width: 100%; min-width: 0; min-height: 100vh;
          padding: 20px 24px 80px; font-family: var(--font);
          background: linear-gradient(160deg, #f0f4ff 0%, #f8fafc 45%, #eef2ff 100%);
          color: var(--text-primary);
          position: relative;
        }

        .el-hero-card {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 52%, #2563eb 100%);
          border: none;
          border-radius: var(--radius-lg);
          padding: 24px 28px;
          margin-bottom: 18px;
          box-shadow: 0 8px 28px rgba(30,58,138,.28);
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          position: relative;
          overflow: hidden;
        }
        .el-hero-card::before {
          content: '';
          position: absolute; top: -60px; right: -60px;
          width: 200px; height: 200px; border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,.1) 0%, transparent 70%);
          pointer-events: none;
        }
        .el-hero-card > * { position: relative; z-index: 1; }
        .el-hero-card .el-title { color: #fff; font-size: 1.45rem; font-weight: 700; letter-spacing: -0.02em; }
        .el-hero-card .el-subtitle { color: rgba(255,255,255,.9); font-size: 0.88rem; margin-top: 4px; font-weight: 500; }
        .el-hero-stat {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,.14); border: 1px solid rgba(255,255,255,.22);
          border-radius: 999px; padding: 8px 16px;
          font-size: 0.82rem; font-weight: 700; color: #fff;
        }
        .el-hero-stat svg { color: rgba(255,255,255,.9); }

        /* ─── Header toolbar ─── */
        .el-toolbar {
          display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
          gap: 12px; margin-bottom: 14px;
          padding: 14px 16px; background: var(--surface);
          border: 1px solid var(--border); border-radius: var(--radius-lg);
          box-shadow: var(--shadow);
        }
        .el-toolbar-left, .el-toolbar-right { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
        .el-filter-select {
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 8px 10px;
          font-size: 0.82rem;
          font-family: var(--font);
          background: var(--surface);
          color: var(--text-primary);
          min-width: 140px;
          max-width: 180px;
          cursor: pointer;
        }
        .el-filter-select:focus {
          outline: none;
          border-color: var(--border-strong);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
        .el-header { display: none; }
        .el-title { font-size: 1.35rem; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
        .el-subtitle { font-size: 0.8rem; color: var(--text-muted); margin-top: 2px; }
        .el-header-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
        .el-active-filter {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 0.82rem; color: var(--text-secondary); cursor: pointer; user-select: none;
          white-space: nowrap;
        }
        .el-active-filter input { accent-color: var(--accent); cursor: pointer; }

        /* ─── Search ─── */
        .search-wrap { position: relative; }
        .search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
        .search-input {
          border: 1.5px solid var(--border);
          background: var(--surface);
          padding: 8px 12px 8px 34px;
          border-radius: var(--radius);
          font-size: 0.85rem;
          font-family: var(--font);
          color: var(--text-primary);
          width: min(340px, 42vw);
          transition: border-color .15s, box-shadow .15s;
        }
        .search-input::placeholder { color: #0a0a0a; opacity: 0.45; }
        .search-input:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,.1); }

        /* ─── Buttons ─── */
        .btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          background: linear-gradient(135deg, var(--royal) 0%, var(--royal-mid) 100%);
          color: #fff;
          border: none; border-radius: var(--radius); padding: 9px 16px;
          font-size: 0.85rem; font-weight: 600; font-family: var(--font);
          cursor: pointer; transition: transform .12s, box-shadow .12s;
          box-shadow: 0 2px 8px rgba(30,58,138,.35);
        }
        .btn-primary:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(30,58,138,.4); }
        .btn-primary:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        .btn-filter {
          display: inline-flex; align-items: center; gap: 7px;
          background: #fff; color: var(--royal);
          border: 1.5px solid #93c5fd; border-radius: var(--radius);
          padding: 8px 14px; font-size: 0.85rem; font-weight: 600;
          font-family: var(--font); cursor: pointer; transition: all .12s;
          position: relative;
        }
        .btn-filter:hover, .btn-filter.is-open { background: var(--accent-light); border-color: var(--accent); }
        .btn-filter.has-active { background: var(--accent-light); border-color: var(--accent); color: var(--navy); }
        .btn-filter-badge {
          min-width: 18px; height: 18px; padding: 0 5px;
          border-radius: 999px; background: var(--royal); color: #fff;
          font-size: 0.68rem; font-weight: 700; display: inline-flex;
          align-items: center; justify-content: center;
        }

        .el-filter-wrap { position: relative; }
        .el-filter-panel {
          position: absolute; top: calc(100% + 8px); left: 0; z-index: 40;
          min-width: 260px; padding: 14px;
          background: var(--surface); border: 1.5px solid var(--border);
          border-radius: var(--radius-lg); box-shadow: var(--shadow-lg);
        }
        .el-filter-panel-title {
          font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.06em; color: var(--text-primary); margin-bottom: 10px;
        }
        .el-filter-option {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 10px; border-radius: 8px; cursor: pointer;
          font-size: 0.85rem; color: var(--text-primary); user-select: none;
          transition: background .1s;
        }
        .el-filter-option:hover { background: var(--accent-light); }
        .el-filter-option input { accent-color: var(--royal); width: 16px; height: 16px; cursor: pointer; }
        .el-filter-divider { height: 1px; background: var(--border); margin: 10px 0; }
        .el-filter-clear {
          width: 100%; margin-top: 8px; padding: 8px;
          border: none; border-radius: 8px; background: #fef2f2;
          color: var(--danger); font-size: 0.8rem; font-weight: 600;
          cursor: pointer; font-family: var(--font);
        }
        .el-filter-clear:hover { background: #fee2e2; }

        .el-active-pills { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
        .el-active-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 999px; font-size: 0.75rem; font-weight: 600;
          background: var(--accent-light); color: var(--royal); border: 1px solid #93c5fd;
        }
        .el-active-pill button {
          border: none; background: transparent; color: var(--royal);
          cursor: pointer; font-size: 0.9rem; line-height: 1; padding: 0;
        }
        .el-type-clear {
          border: none; background: transparent; color: var(--danger);
          font-size: 0.75rem; font-weight: 600; cursor: pointer; padding: 4px 8px;
        }

        .btn-ghost {
          display: inline-flex; align-items: center; gap: 6px;
          background: transparent; color: var(--text-secondary);
          border: 1.5px solid var(--border); border-radius: var(--radius); padding: 8px 16px;
          font-size: 0.85rem; font-weight: 500; font-family: var(--font);
          cursor: pointer; transition: border-color .15s, color .15s;
        }
        .btn-ghost:hover { border-color: var(--border-strong); color: var(--text-primary); }
        .btn-ghost:disabled { opacity: .5; cursor: not-allowed; }

        .el-export-wrap { position: relative; }
        .el-export-menu {
          position: absolute; top: calc(100% + 6px); right: 0; z-index: 30;
          min-width: 168px; padding: 6px;
          background: var(--surface); border: 1.5px solid var(--border);
          border-radius: var(--radius); box-shadow: var(--shadow-lg);
        }
        .el-export-item {
          display: flex; align-items: center; gap: 8px; width: 100%;
          border: none; background: transparent; text-align: left;
          padding: 9px 10px; border-radius: 6px; font-size: 0.84rem;
          font-family: var(--font); color: var(--text-primary); cursor: pointer;
        }
        .el-export-item:hover { background: var(--accent-light); color: var(--accent); }
        .el-export-item:disabled { opacity: .5; cursor: not-allowed; }

        .btn-danger {
          display: inline-flex; align-items: center; gap: 6px;
          background: var(--danger); color: #fff;
          border: none; border-radius: var(--radius); padding: 8px 16px;
          font-size: 0.85rem; font-weight: 500; font-family: var(--font);
          cursor: pointer; transition: background .15s;
        }
        .btn-danger:hover { background: var(--danger-hover); }
        .btn-danger:disabled { opacity: .6; cursor: not-allowed; }

        .btn-back {
          position: fixed; bottom: 24px; right: 24px; z-index: 100;
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--text-primary); color: #fff;
          border: none; border-radius: 50px; padding: 11px 20px;
          font-size: 0.85rem; font-weight: 500; font-family: var(--font);
          cursor: pointer; box-shadow: var(--shadow-lg); transition: transform .15s, background .15s;
        }
        .btn-back:hover { background: #1a1f2e; transform: translateY(-1px); }

        /* ─── Employee type badges in table ─── */
        .el-type-badge {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 999px; font-size: 0.72rem; font-weight: 700;
          letter-spacing: 0.02em; border: 1px solid transparent;
        }
        .el-type-badge.type-permanent { background: #dbeafe; color: #1e40af; border-color: #93c5fd; }
        .el-type-badge.type-temporary { background: #fef3c7; color: #b45309; border-color: #fcd34d; }
        .el-type-badge.type-contract { background: #ede9fe; color: #6d28d9; border-color: #c4b5fd; }
        .el-type-badge.type-intern { background: #ccfbf1; color: #0f766e; border-color: #5eead4; }
        .el-type-badge.type-default { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }

        /* ─── Table container ─── */
        .table-wrap {
          border: 1px solid var(--border); border-radius: var(--radius-lg);
          background: var(--surface); overflow: hidden;
          box-shadow: var(--shadow);
        }
        .table-scroll { overflow-x: auto; max-height: calc(100vh - 280px); overflow-y: auto; }

        /* ─── Table ─── */
        table { width: 100%; border-collapse: collapse; font-size: 0.845rem; }
        thead { position: sticky; top: 0; z-index: 10; }
        thead tr {
          background: #e8eef5;
          border-bottom: 1px solid #d5dde8;
        }
        th {
          padding: 11px 14px; text-align: left; font-weight: 600;
          font-size: 0.8rem; letter-spacing: 0.01em;
          color: #334155; white-space: nowrap;
        }
        .el-th-actions { text-align: center; width: 88px; }
        tbody tr { border-bottom: 1px solid #e8edf3; transition: background .12s; background: #fff; }
        tbody tr:nth-child(even) { background: #fff; }
        tbody tr:nth-child(odd) { background: #fff; }
        tbody tr:last-child { border-bottom: none; }
        tbody tr:hover { background: var(--row-hover) !important; }
        tbody tr.el-row-preview-active { background: #dbeafe !important; box-shadow: inset 4px 0 0 #2563eb; }
        tbody tr.row-updated { animation: rowFlash 2.5s ease forwards; }
        @keyframes rowFlash { 0%,100% { background: transparent; } 10%,40% { background: var(--update-flash); } }
        td { padding: 11px 14px; color: #0a0a0a; font-weight: 500; vertical-align: middle; }
        td.muted { color: #0a0a0a; font-size: 0.82rem; font-weight: 500; }
        .mono { font-family: var(--font-mono); font-size: 0.8rem; color: #0a0a0a; font-weight: 600; }

        /* ─── Employee name cell ─── */
        .emp-name-cell { display: flex; flex-direction: column; }
        .emp-name { font-weight: 700; color: #0a0a0a; }
        .emp-meta { font-size: 0.77rem; color: #0a0a0a; margin-top: 1px; font-weight: 500; }

        /* ─── Status badge ─── */
        .badge { display: inline-block; padding: 2px 9px; border-radius: 50px; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; }
        .badge-active { background: #dbeafe; color: #1d4ed8; }
        .badge-inactive { background: #f1f5f9; color: #64748b; }
        .badge-leave { background: #fef9c3; color: #a16207; }
        .badge-terminated { background: #fee2e2; color: #991b1b; }

        .el-row-clickable { cursor: pointer; transition: background .12s; }
        .el-row-clickable:hover { background: var(--row-hover); }
        .el-emp-name { font-weight: 700; color: #0a0a0a; font-size: 0.88rem; }
        .el-emp-name-link {
          border: none;
          background: none;
          padding: 0;
          cursor: pointer;
          text-align: left;
          color: #4f46e5;
        }
        .el-emp-name-link:hover { text-decoration: underline; }
        .el-fuel-chip {
          margin-left: 8px;
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 1px 7px;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.01em;
          color: #166534;
          background: #dcfce7;
          text-decoration: none;
        }
        .el-emp-code { font-size: 0.75rem; color: #0a0a0a; font-family: var(--font-mono); margin-top: 2px; font-weight: 600; }
        td.muted { color: #0a0a0a; font-size: 0.82rem; font-weight: 500; }

        .el-actions { display: flex; align-items: center; justify-content: center; gap: 6px; }

        .act-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; padding: 0; border-radius: 8px;
          font-family: var(--font); cursor: pointer;
          transition: background .12s, border-color .12s, color .12s;
          background: #fff;
        }
        .act-btn:disabled { opacity: .45; cursor: not-allowed; pointer-events: none; }
        .act-view-icon {
          border: 1.5px solid #c4b5fd;
          color: #6d28d9;
        }
        .act-view-icon:hover:not(:disabled) {
          background: #f5f3ff;
          border-color: #a78bfa;
        }
        .act-edit-icon {
          border: 1.5px solid #93c5fd;
          color: #2563eb;
        }
        .act-edit-icon:hover:not(:disabled) {
          background: #eff6ff;
          border-color: #60a5fa;
        }
        .act-delete-icon {
          border: 1.5px solid #fca5a5;
          color: #dc2626;
        }
        .act-delete-icon:hover:not(:disabled) {
          background: #fef2f2;
          border-color: #f87171;
        }

        /* ─── Empty state ─── */
        .empty-row td { padding: 48px; text-align: center; color: #0a0a0a; font-weight: 500; }

        /* ─── Footer ─── */
        .el-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; flex-wrap: wrap; gap: 12px; }
        .footer-info { font-size: 0.82rem; color: #0a0a0a; font-weight: 600; }
        .rows-select { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; color: #0a0a0a; font-weight: 500; }
        .rows-select select { border: 1.5px solid var(--border); border-radius: 6px; padding: 4px 8px; font-size: 0.82rem; font-family: var(--font); background: var(--surface); cursor: pointer; }
        .pagination { display: flex; align-items: center; gap: 4px; }
        .pg-btn {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 32px; height: 32px; padding: 0 8px;
          border: 1.5px solid var(--border); border-radius: 6px;
          background: var(--surface); color: var(--text-secondary);
          font-size: 0.82rem; font-family: var(--font); cursor: pointer;
          transition: background .12s, border-color .12s, color .12s;
        }
        .pg-btn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
        .pg-btn:disabled { opacity: .4; cursor: not-allowed; }
        .pg-btn.active { background: var(--text-primary); border-color: var(--text-primary); color: #fff; font-weight: 600; }
        .pg-ellipsis { padding: 0 4px; color: var(--text-muted); font-size: 0.82rem; }

        /* ─── Toast ─── */
        .toast {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 12px 14px; border-radius: var(--radius); box-shadow: var(--shadow-lg);
          font-size: 0.85rem; font-family: var(--font); font-weight: 450;
          border: 1.5px solid transparent;
        }
        .toast-success { background: var(--success-light); border-color: #86efac; color: #15803d; }
        .toast-error { background: var(--danger-light); border-color: #fca5a5; color: #991b1b; }
        .toast-icon { flex-shrink: 0; margin-top: 1px; }
        .toast-msg { flex: 1; line-height: 1.4; }
        .toast-close { background: none; border: none; cursor: pointer; color: inherit; opacity: .6; padding: 0; margin-left: 4px; display: flex; align-items: center; }
        .toast-close:hover { opacity: 1; }

        /* ─── Responsive ─── */
        @media (max-width: 1024px) {
          .el-page { padding: 20px 18px 84px; }
          .search-input { width: min(360px, 48vw); }
          .btn-back { right: 16px; bottom: 16px; padding: 10px 16px; }
        }

        @media (max-width: 768px) {
          .el-page { padding: 16px 14px 88px; }
          .el-toolbar { flex-direction: column; align-items: stretch; }
          .el-toolbar-left, .el-toolbar-right { width: 100%; }
          .el-toolbar-right { justify-content: stretch; }
          .el-toolbar-right .btn-primary { flex: 1; justify-content: center; }
          .search-wrap { width: 100%; }
          .search-input { width: 100%; }
          .btn-primary, .btn-ghost, .btn-danger, .btn-filter { min-height: 44px; }
          .table-scroll { max-height: none; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .drawer-grid, .preview-grid { grid-template-columns: 1fr; }
          .drawer {
            top: auto; left: 0; right: 0; bottom: 0;
            width: 100%; max-width: 100%;
            max-height: 85vh;
            border-radius: 16px 16px 0 0;
            animation: slideUp .28s cubic-bezier(.25,.46,.45,.94);
          }
          .drawer-overlay { background: rgba(0,0,0,.45); }
          .el-actions { gap: 6px; }
          .act-btn span.btn-label { display: none; }
          th:nth-child(3), td:nth-child(3) { display: none; }
          .modal-box { padding: 22px; width: 94%; }
        }

        @media (max-width: 420px) {
          .btn-back { width: calc(100% - 28px); left: 14px; right: 14px; justify-content: center; }
        }
        /* ─── Modal ─── */
        .modal-overlay {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(15,17,23,.45); backdrop-filter: blur(3px);
          display: flex; align-items: center; justify-content: center;
          animation: fadeIn .15s ease;
        }
        .modal-box {
          background: var(--surface); border-radius: var(--radius-lg);
          padding: 32px; max-width: 420px; width: 90%; box-shadow: var(--shadow-xl);
          animation: scaleIn .15s ease;
        }
        .modal-icon-wrap { width: 48px; height: 48px; border-radius: 50%; background: var(--danger-light); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; color: var(--danger); }
        .modal-title { font-size: 1.05rem; font-weight: 600; margin-bottom: 10px; }
        .modal-body { font-size: 0.88rem; color: var(--text-secondary); line-height: 1.55; margin-bottom: 24px; }
        .modal-body strong { color: var(--text-primary); }
        .modal-actions { display: flex; justify-content: flex-end; gap: 10px; }

        /* ─── Drawer ─── */
        .drawer-overlay {
          position: fixed; inset: 0; z-index: 800;
          background: rgba(15,17,23,.35); backdrop-filter: blur(2px);
          animation: fadeIn .2s ease;
        }
        .drawer {
          position: fixed; top: 0; right: 0; bottom: 0; z-index: 900;
          width: 480px; max-width: 95vw;
          background: var(--surface); box-shadow: var(--shadow-xl);
          display: flex; flex-direction: column;
          animation: slideInRight .22s cubic-bezier(.25,.46,.45,.94);
        }
        .drawer-header {
          display: flex; align-items: center; gap: 14px;
          padding: 20px 24px; border-bottom: 1px solid var(--border);
          background: #fafafa; flex-shrink: 0;
        }
        .preview-drawer-header { background: #fff; }
        .drawer-avatar {
          width: 52px; height: 52px; border-radius: 14px;
          background: linear-gradient(135deg, #1e3a8a 0%, #f59e0b 100%);
          color: #fff; font-weight: 700; font-size: 1rem;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; letter-spacing: 0.03em;
        }
        .drawer-title { font-size: 1.05rem; font-weight: 700; color: var(--text-primary); }
        .drawer-subtitle { font-size: 0.8rem; color: var(--text-muted); margin-top: 3px; font-family: var(--font-mono); font-weight: 500; }
        .drawer-close {
          margin-left: auto; background: none; border: none; cursor: pointer;
          color: var(--text-muted); padding: 6px; border-radius: 6px;
          display: flex; align-items: center; transition: color .12s, background .12s;
        }
        .drawer-close:hover { color: var(--text-primary); background: var(--border); }

        .drawer-body { flex: 1; overflow-y: auto; padding: 20px 24px; }
        .drawer-section-label {
          font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.08em; color: var(--text-muted); margin-bottom: 14px;
        }
        .preview-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 20px; }
        .preview-field { display: flex; flex-direction: column; gap: 4px; }
        .preview-field-label { font-size: 0.72rem; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; }
        .preview-field-value { font-size: 0.9rem; font-weight: 600; color: var(--text-primary); word-break: break-word; }

        .drawer-footer {
          display: flex; align-items: center; gap: 10px; justify-content: flex-end;
          padding: 16px 24px; border-top: 1px solid var(--border); flex-shrink: 0;
          background: #fafafa;
        }
        .preview-drawer-footer { background: #fff; }
        .btn-preview-ghost {
          padding: 10px 16px; border-radius: 8px; border: 1px solid var(--border);
          background: #fff; color: var(--text-secondary); font-weight: 600; font-size: 0.85rem;
          cursor: pointer; font-family: var(--font);
        }
        .btn-preview-primary {
          padding: 10px 18px; border-radius: 8px; border: none;
          background: linear-gradient(135deg, #ea580c 0%, #f59e0b 100%);
          color: #fff; font-weight: 700; font-size: 0.85rem; cursor: pointer;
          font-family: var(--font); box-shadow: 0 2px 10px rgba(245,158,11,.35);
        }
        .btn-preview-primary:hover { filter: brightness(1.05); }

        .drawer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

        .field-group { display: flex; flex-direction: column; gap: 5px; }
        .field-group.field-dirty .field-input { border-color: var(--accent); background: var(--accent-light); }
        .field-label { font-size: 0.78rem; font-weight: 500; color: var(--text-secondary); display: flex; align-items: center; gap: 6px; }
        .required-star { color: var(--danger); }
        .field-changed-badge { background: var(--accent); color: #fff; border-radius: 4px; font-size: 0.65rem; font-weight: 600; padding: 1px 5px; }
        .field-input {
          border: 1.5px solid var(--border); border-radius: 6px; padding: 8px 10px;
          font-size: 0.84rem; font-family: var(--font); color: var(--text-primary);
          background: var(--surface); transition: border-color .12s, background .12s, box-shadow .12s;
          width: 100%;
        }
        .field-input:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,.08); }

        .drawer-footer {
          display: flex; align-items: center; gap: 10px; justify-content: flex-end;
          padding: 16px 24px; border-top: 1.5px solid var(--border); flex-shrink: 0;
          background: #f8f9fb;
        }
        .changes-badge { font-size: 0.78rem; color: var(--accent); font-weight: 500; margin-right: auto; }

        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }

        /* ─── Spin ─── */
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin-icon { animation: spin .8s linear infinite; }

        /* ─── Animations ─── */
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(.94); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>

      <div className={`el-page${embedded ? " el-embedded" : ""}`}>
        {/* Toasts */}
        <Toast toasts={toasts} removeToast={removeToast} />

        {/* Hero — hidden when embedded in unified page */}
        {!embedded && (
        <div className="el-hero-card">
          <div>
            <div className="el-title">Employee List</div>
            <div className="el-subtitle">
              Manage your workforce — search, filter, and update records in one place.
            </div>
          </div>
          <div className="el-hero-stat">
            <IconUser />
            {totalCount} active record{totalCount !== 1 ? "s" : ""}
          </div>
        </div>
        )}

        {!embedded && (
        <div style={{ marginBottom: 14 }}>
          <SetupIncompleteBanner compact />
        </div>
        )}

        {missingVerifiedBankOnly ? (
          <div
            style={{
              marginBottom: 14,
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              fontSize: 13.5,
              fontWeight: 600,
              lineHeight: 1.45,
            }}
            role="status"
          >
            {totalCount} employee{totalCount === 1 ? "" : "s"} missing a verified bank account.
            Click <strong>Edit</strong> on a row to open the Banking step and add / verify bank details.
          </div>
        ) : null}

        {/* Toolbar */}
        <div className="el-toolbar">
          <div className="el-toolbar-left">
            <div className="search-wrap">
              <span className="search-icon"><IconSearch /></span>
              <input
                className="search-input"
                type="text"
                placeholder="Search name, code, email, department, designation…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="el-filter-wrap" ref={filterMenuRef}>
              <button
                type="button"
                className={`btn-filter${filterMenuOpen ? " is-open" : ""}${activeFilterCount ? " has-active" : ""}`}
                onClick={() => setFilterMenuOpen((o) => !o)}
              >
                <IconFilter />
                Filters
                {activeFilterCount > 0 && (
                  <span className="btn-filter-badge">{activeFilterCount}</span>
                )}
              </button>

              {filterMenuOpen && (
                <div className="el-filter-panel">
                  <div className="el-filter-panel-title">Employee type</div>
                  {LIST_FILTER_TYPES.map((type) => (
                    <label key={type} className="el-filter-option">
                      <input
                        type="checkbox"
                        checked={employeeTypeFilter.includes(type)}
                        onChange={() => toggleEmployeeTypeFilter(type)}
                      />
                      <TypeBadge type={type} />
                    </label>
                  ))}
                  <div className="el-filter-divider" />
                  <label className="el-filter-option">
                    <input
                      type="checkbox"
                      checked={!activeOnly}
                      onChange={(e) => setActiveOnly(!e.target.checked)}
                    />
                    <span>Include inactive employees</span>
                  </label>
                  {activeFilterCount > 0 && (
                    <button type="button" className="el-filter-clear" onClick={clearAllFilters}>
                      Clear all filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="el-toolbar-right">
            <div className="el-export-wrap" ref={exportMenuRef}>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setExportMenuOpen((open) => !open)}
                disabled={exporting}
                title="Export employee list"
              >
                {exporting ? "Exporting…" : "Export ▾"}
              </button>
              {exportMenuOpen && !exporting && (
                <div className="el-export-menu">
                  <button type="button" className="el-export-item" onClick={() => handleExport("csv")}>
                    Export CSV
                  </button>
                  <button type="button" className="el-export-item" onClick={() => handleExport("xlsx")}>
                    Export Excel (.xlsx)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {activeFilterCount > 0 && (
          <div className="el-active-pills">
            {employeeTypeFilter.map((type) => (
              <span key={type} className="el-active-pill">
                {type}
                <button type="button" onClick={() => toggleEmployeeTypeFilter(type)} aria-label={`Remove ${type}`}>×</button>
              </span>
            ))}
            {!activeOnly && (
              <span className="el-active-pill">
                Inactive included
                <button type="button" onClick={() => setActiveOnly(true)} aria-label="Hide inactive">×</button>
              </span>
            )}
            <button type="button" className="el-type-clear" onClick={clearAllFilters}>
              Clear all
            </button>
          </div>
        )}

        {/* Table */}
        {loading && employees.length === 0 ? (
          <PageLoader />
        ) : (
        <>
        <div className="table-wrap">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th className="el-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && totalCount === 0 && (
                  <tr className="empty-row">
                    <td colSpan={8}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "32px 16px" }}>
                        <div style={{ color: "#94a3b8", lineHeight: 1 }}>
                          <IconUsers />
                        </div>
                        <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b", margin: 0 }}>
                          {debouncedSearch ? "No employees match your search" : "Your workforce is empty"}
                        </p>
                        <p style={{ fontSize: "0.85rem", color: "#64748b", margin: 0 }}>
                          {debouncedSearch
                            ? "Try a different name, code, department, or designation."
                            : "Add your first employee to get started with payroll."}
                        </p>
                        {!debouncedSearch && (
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                            <button
                              type="button"
                              onClick={handleAddClick}
                              style={{ background: "#4f46e5", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 16px", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer" }}
                            >
                              + Add Employee
                            </button>
                            <button
                              type="button"
                              onClick={handleBulkClick}
                              style={{ background: "#fff", color: "#4f46e5", border: "1.5px solid #4f46e5", borderRadius: "8px", padding: "8px 16px", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer" }}
                            >
                              Bulk Import
                            </button>
                          </div>
                        )}
                        {obCtx?.progress && !obCtx.progress.is_complete && !debouncedSearch && (
                          <p style={{ fontSize: "0.75rem", color: "#94a3b8", margin: 0 }}>
                            Step 2 of your setup guide — adding employees unlocks payroll processing.
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && totalCount > 0 && currentEmployees.length === 0 && (
                  <tr className="empty-row">
                    <td colSpan={8}>No employees on this page.</td>
                  </tr>
                )}

                {!loading &&
                  currentEmployees.map((emp) => (
                    <tr
                      key={emp.employee_id}
                      className={`el-row-clickable${previewEmp?.employee_id === emp.employee_id ? " el-row-preview-active" : ""}`}
                      onClick={() => handleRowClick(emp)}
                      title="Open employee profile"
                    >
                      <td className="mono">{emp.employee_code || "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="el-emp-name el-emp-name-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToProfile(emp);
                          }}
                        >
                          {fullName(emp) || "—"}
                          {fuelEnabled && (emp.metadata || emp.extra_metadata || {}).fuel_reimbursement_eligible ? (
                            <span className="el-fuel-chip">Fuel eligible</span>
                          ) : null}
                        </button>
                      </td>
                      <td className="muted">{emp.department?.name || "—"}</td>
                      <td className="muted">{designationLabel(emp)}</td>
                      <td className="muted">{emp.work_email || emp.email || "—"}</td>
                      <td className="muted">{phoneLabel(emp)}</td>
                      <td>
                        <StatusBadge status={emp.status} />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="el-actions">
                          <button
                            type="button"
                            className="act-btn act-view-icon"
                            onClick={() => goToProfile(emp)}
                            disabled={actionLoadingId === emp.employee_id}
                            aria-label="View employee profile"
                            title="View Profile"
                          >
                            <IconView />
                          </button>
                          <button
                            type="button"
                            className="act-btn act-edit-icon"
                            onClick={() => goToEdit(emp)}
                            disabled={actionLoadingId === emp.employee_id}
                            aria-label="Edit employee"
                            title="Edit"
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="act-btn act-delete-icon"
                            onClick={() => setDeleteTarget(emp)}
                            disabled={actionLoadingId === emp.employee_id}
                            aria-label="Delete employee"
                            title="Delete"
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="el-footer">
          <div className="footer-info">
            {totalCount > 0
              ? `Showing ${firstIndex} to ${lastIndex} of ${totalCount} employees`
              : "No results"}
          </div>

          <div className="rows-select">
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[10, 15, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {totalCount > 0 && (
            <div className="pagination">
              <button className="pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                <IconChevronLeft /><IconChevronLeft />
              </button>
              <button className="pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                <IconChevronLeft />
              </button>
              {currentPage > 3 && <span className="pg-ellipsis">…</span>}
              {getPageNumbers().map((n) => (
                <button key={n} className={`pg-btn ${currentPage === n ? "active" : ""}`} onClick={() => setCurrentPage(n)}>
                  {n}
                </button>
              ))}
              {currentPage < totalPages - 2 && <span className="pg-ellipsis">…</span>}
              <button
                className="pg-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <IconChevronRight />
              </button>
              <button
                className="pg-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                <IconChevronRight /><IconChevronRight />
              </button>
            </div>
          )}
        </div>
        </>
        )}

      {/* Preview drawer */}
      {previewEmp && (
        <EmployeePreviewDrawer
          employee={previewEmp}
          onClose={closePreview}
          onOpenFull={(emp) => { closePreview(); goToProfile(emp); }}
        />
      )}

      </div>

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          employee={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => { if (!deleteLoading) setDeleteTarget(null); }}
          loading={deleteLoading}
        />
      )}
    </>
  );
}
